import React, { useEffect, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import CaseStudyBg from "../assets/images/case-study-bg.webp";
import { Link } from "react-router-dom";
import { CaseStudiesSkeleton, EmptyState } from "../components/SkeletonUI";
import EnquiryModal from "../components/EnquiryModal";

/* ─── Category pill ─────────────────────────────────────────────── */
function CategoryPill({ label }) {
  if (!label) return null;
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 12px",
      borderRadius: 20,
      background: "#EFF6FF",
      color: "#009EDC",
      fontSize: "0.7rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    }}>
      {label}
    </span>
  );
}

/* ─── Featured horizontal card (first item) ─────────────────────── */
function FeaturedCard({ item }) {
  const imageUrl = item?.attributes?.Image?.data?.attributes?.url;

  return (
    <Link
      to={`/caseStudies/${item.id}`}
      style={{ textDecoration: "none", color: "inherit", display: "block", marginBottom: 48 }}
    >
      <div style={{
        borderRadius: 18,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
        transition: "box-shadow 0.22s",
      }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,0.12)"}
        onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.07)"}
      >
        <div className="row g-0">
          {/* Content */}
          <div className="col-md-6" style={{
            padding: "clamp(32px, 5vw, 56px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}>
            <div style={{ marginBottom: 20 }}>
              <CategoryPill label={item.attributes.category} />
            </div>
            <h2 style={{
              fontWeight: 800,
              fontSize: "clamp(1.3rem, 2vw, 1.75rem)",
              color: "#0a1a3a",
              lineHeight: 1.25,
              marginBottom: 16,
            }}>
              {item.attributes.Title}
            </h2>
            {(item.attributes.summary || item.attributes.overview) && (
              <p style={{
                color: "#4B5563",
                lineHeight: 1.75,
                marginBottom: 28,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}>
                {item.attributes.summary || item.attributes.overview}
              </p>
            )}
            <span style={{ color: "#009EDC", fontWeight: 700, fontSize: "0.9rem" }}>
              Read case study →
            </span>
          </div>

          {/* Image */}
          <div className="col-md-6" style={{ minHeight: 280 }}>
            <img
              src={imageUrl}
              alt={item.attributes.Title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ─── Regular grid card ─────────────────────────────────────────── */
function CaseCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const imageUrl = item?.attributes?.Image?.data?.attributes?.url;

  return (
    <Link
      to={`/caseStudies/${item.id}`}
      style={{ textDecoration: "none", color: "inherit", display: "block", height: "100%" }}
    >
      <article
        style={{
          borderRadius: 14,
          overflow: "hidden",
          background: "#fff",
          border: "1px solid #e5e7eb",
          transition: "box-shadow 0.22s ease, transform 0.22s ease",
          boxShadow: hovered ? "0 12px 32px rgba(0,0,0,0.10)" : "0 2px 8px rgba(0,0,0,0.05)",
          transform: hovered ? "translateY(-4px)" : "translateY(0)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={{ height: 220, overflow: "hidden", flexShrink: 0 }}>
          <img
            src={imageUrl}
            alt={item.attributes.Title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.4s ease",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          />
        </div>

        <div style={{ padding: "20px 22px 26px", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ marginBottom: 12 }}>
            <CategoryPill label={item.attributes.category} />
          </div>

          <h6 style={{
            fontWeight: 700,
            color: "#0a1a3a",
            marginBottom: 10,
            lineHeight: 1.4,
            fontSize: "0.95rem",
          }}>
            {item.attributes.Title}
          </h6>

          {(item.attributes.summary || item.attributes.overview) && (
            <p style={{
              color: "#6B7280",
              fontSize: "0.82rem",
              lineHeight: 1.65,
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              marginBottom: 0,
            }}>
              {item.attributes.summary || item.attributes.overview}
            </p>
          )}

          <span style={{ color: "#009EDC", fontSize: "0.82rem", fontWeight: 700, marginTop: 16 }}>
            Read more →
          </span>
        </div>
      </article>
    </Link>
  );
}

/* ─── Page ──────────────────────────────────────────────────────── */
export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetchFromStrapi("/ecl-case-study-sections");
        setCases(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch case studies", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) return <CaseStudiesSkeleton />;

  const featured = cases[0];
  const rest = cases.slice(1);

  return (
    <main>
      {/* HERO */}
      <section style={{
        position: "relative",
        height: 520,
        backgroundImage: `url(${CaseStudyBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="hero-info-card">
            <p className="text-uppercase fw-semibold mb-2"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
              Client success stories
            </p>
            <h1 className="fw-bold mb-3" style={{ fontSize: "clamp(2.1rem, 4vw, 3.2rem)", lineHeight: 1.15, color: "#0a1a3a" }}>
              Case Studies
            </h1>
            <p className="mb-0" style={{ color: "#4B5563", lineHeight: 1.7 }}>
              Real problems. Real clients. Real results. See how ECL has helped
              organisations across Africa modernise, secure, and scale their operations.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container" style={{ paddingTop: 80, paddingBottom: 80 }}>
        {cases.length === 0 ? (
          <EmptyState
            title="No case studies yet"
            description="Our team is documenting client success stories. Come back soon to see how ECL is making an impact."
            action={{ label: "Explore our solutions", href: "/services" }}
          />
        ) : (
          <>
            <p className="text-uppercase fw-semibold mb-2"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
              Featured story
            </p>
            <h2 className="fw-bold mb-5"
              style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}>
              Latest Work
            </h2>

            {/* Featured — horizontal card */}
            {featured && <FeaturedCard item={featured} />}

            {/* Rest — grid */}
            {rest.length > 0 && (
              <>
                <h3 className="fw-bold mb-4" style={{ color: "#0a1a3a" }}>
                  More stories
                </h3>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 28,
                }}>
                  {rest.map(item => (
                    <CaseCard key={item.id} item={item} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* CTA */}
      <section style={{ background: "#0a1a3a", padding: "88px 0" }}>
        <div className="container text-center">
          <p className="text-uppercase fw-semibold mb-3"
            style={{ fontSize: "0.75rem", letterSpacing: "0.14em", color: "#009EDC" }}>
            Work with us
          </p>
          <h2 className="fw-bold mb-3"
            style={{ color: "#fff", fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
            Ready to become our next success story?
          </h2>
          <p style={{
            color: "rgba(255,255,255,0.6)",
            maxWidth: 480,
            margin: "0 auto 40px",
            lineHeight: 1.75,
          }}>
            Our team is ready to help you tackle your most pressing technology challenges.
          </p>
          <button
            className="btn btn-lg px-5 fw-semibold"
            style={{ backgroundColor: "#009EDC", color: "#fff", borderRadius: 8 }}
            onClick={() => setModalOpen(true)}
          >
            Start a conversation
          </button>
        </div>
      </section>

      <EnquiryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}
